import { ApiFetch } from '../../../shared/utils/fetch';
import { unwrap, type Enveloped } from '../../../shared/utils/unwrap';

// Booking medium values — mirror booking-service's BookingMedium enum
// (platform-services/booking-service .../create-booking-service.dto.ts).
export const BOOKING_MEDIUMS = ['IN_PERSON', 'VIDEO_CALL', 'PHONE', 'ANY'] as const;
export type BookingMedium = (typeof BOOKING_MEDIUMS)[number];

// Booking category values — mirror booking-service's BookingCategory enum.
export const BOOKING_CATEGORIES = [
  'STYLING',
  'HAIR',
  'BEAUTY',
  'EVENT_PLANNING',
  'INTERIOR',
  'CUSTOM_ORDER',
  'FOOD_TASTING',
  'GENERAL',
] as const;
export type BookingCategory = (typeof BOOKING_CATEGORIES)[number];

export interface BookingService {
  id: string;
  tenantId: string;
  solutionId: string;
  title: string;
  description: string | null;
  category: BookingCategory;
  durationMins: number;
  /** Fee in kobo (minor units). 0 when not charging. */
  fee: number;
  currency: string;
  /** The "charge customers for this booking" toggle. */
  chargingEnabled: boolean;
  medium: BookingMedium;
  maxBookingsPerSlot: number;
  bufferMins?: number;
  isActive: boolean;
  imageUrl?: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface CreateBookingServicePayload {
  tenantId: string;
  solutionId: string;
  title: string;
  description?: string;
  category: BookingCategory;
  durationMins: number;
  /** Fee in kobo (minor units). */
  fee: number;
  currency?: string;
  chargingEnabled: boolean;
  medium?: BookingMedium;
  maxBookingsPerSlot?: number;
  isActive?: boolean;
}

export type UpdateBookingServicePayload = Partial<
  Omit<CreateBookingServicePayload, 'tenantId' | 'solutionId'>
>;

/**
 * Calls the osprey-api bookings routes (PE-1875). osprey-api scopes by the
 * admin's selected tenant via the `x-tenant-id` header, which `ApiFetch`
 * derives from `selectedTenantId` (see shared/utils/fetch.ts). The body is
 * forwarded as-is to booking-service, so it carries
 * tenantId/solutionId/chargingEnabled/etc.
 *
 * osprey-api exposes `@Post('services')` (create) and `@Patch('services/:id')`
 * (update) under `/bookings`. `ApiFetch.put` issues a PATCH on the wire (see
 * shared/utils/fetch.ts), so update maps to `PATCH /bookings/services/:id`.
 */
export const bookingServicesApi = {
  list: async (): Promise<BookingService[]> => {
    const response = await ApiFetch.get<Enveloped<BookingService[]>>('/bookings/services');
    return unwrap(response);
  },

  create: async (data: CreateBookingServicePayload): Promise<BookingService> => {
    const response = await ApiFetch.post<Enveloped<BookingService>>('/bookings/services', data);
    return unwrap(response);
  },

  update: async (id: string, data: UpdateBookingServicePayload): Promise<BookingService> => {
    // ApiFetch.put issues a PATCH (see shared/utils/fetch.ts) → PATCH /bookings/services/:id.
    const response = await ApiFetch.put<Enveloped<BookingService>>(`/bookings/services/${id}`, data);
    return unwrap(response);
  },
};
